/**
 * charts — the dashboard's chart primitives, built on Recharts. All charts are
 * themed with Geist tokens (grid / axis / tooltip pull from var(--…)), so they
 * stay on-brand and follow light/dark. Tooltips + legend + responsive sizing
 * come from the library (the hand-rolled SVG had none of those).
 *
 *   SeriesChart      — N line OR grouped-bar series over a shared x axis (dates)
 *   DistributionChart — horizontal bar breakdown of {label,value}[] (metrics)
 *   BarRateChart     — daily bars (left axis) + a 0–100 % line (right axis)
 */
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export interface ChartSeries {
  key: string; // data field
  name: string; // legend/tooltip label
  color: string; // token var() or hex
}

const AXIS_TICK = { fill: 'var(--text-subtle)', fontSize: 11 } as const;
const GRID_STROKE = 'var(--border-subtle)';
const TOOLTIP_STYLE = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-caption)',
  color: 'var(--text)',
} as const;
const LEGEND_STYLE = { fontSize: 'var(--text-caption)' } as const;

// 'YYYY-MM-DD' → 'MM-DD' for compact date ticks.
const dayTick = (v: unknown) => (typeof v === 'string' && v.length >= 5 ? v.slice(5) : String(v));

function ChartEmpty({ height }: { height: number }) {
  return (
    <div className="chart-empty" style={{ height }}>
      此區間尚無資料
    </div>
  );
}

interface SeriesChartProps {
  type: 'line' | 'bar';
  // Accept interface-typed arrays (e.g. AnalyticsDailyPoint[]) without index-signature friction.
  data: object[];
  series: ChartSeries[];
  xKey?: string;
  height?: number;
}

export function SeriesChart({ type, data, series, xKey = 'date', height = 260 }: SeriesChartProps) {
  const allZero = data.every((d) => series.every((s) => !(d as Record<string, unknown>)[s.key]));
  if (data.length === 0 || series.length === 0 || allZero) return <ChartEmpty height={height} />;

  const common = (
    <>
      <CartesianGrid stroke={GRID_STROKE} vertical={false} />
      <XAxis dataKey={xKey} tick={AXIS_TICK} tickFormatter={dayTick} tickLine={false} axisLine={{ stroke: GRID_STROKE }} minTickGap={24} />
      <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} allowDecimals={false} width={32} />
      <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ color: 'var(--text-muted)' }} cursor={{ fill: 'var(--surface-2)', opacity: 0.4 }} />
      {series.length > 1 && <Legend wrapperStyle={LEGEND_STYLE} />}
    </>
  );

  return (
    <ResponsiveContainer width="100%" height={height}>
      {type === 'line' ? (
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          {common}
          {series.map((s) => (
            <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
          ))}
        </LineChart>
      ) : (
        <BarChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          {common}
          {series.map((s) => (
            <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={[2, 2, 0, 0]} maxBarSize={40} />
          ))}
        </BarChart>
      )}
    </ResponsiveContainer>
  );
}

interface DistributionDatum {
  label: string;
  value: number;
}

export function DistributionChart({
  data,
  color = 'var(--primary)',
  height = 160,
}: {
  data: DistributionDatum[];
  color?: string;
  height?: number;
}) {
  const allZero = data.every((d) => !d.value);
  if (data.length === 0 || allZero) return <ChartEmpty height={height} />;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
        <CartesianGrid stroke={GRID_STROKE} horizontal={false} />
        <XAxis type="number" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: GRID_STROKE }} allowDecimals={false} />
        <YAxis type="category" dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={false} width={84} />
        <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'var(--surface-2)', opacity: 0.4 }} />
        <Bar dataKey="value" fill={color} radius={[0, 3, 3, 0]} maxBarSize={26} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Daily count bars on the left axis + a cumulative percentage line (0–100) on the right axis. */
export function BarRateChart({
  data, bar, rate, xKey = 'date', height = 240,
}: {
  data: object[];
  bar: ChartSeries;
  rate: ChartSeries;
  xKey?: string;
  height?: number;
}) {
  if (data.length === 0) return <ChartEmpty height={height} />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 8, right: 4, bottom: 4, left: 0 }}>
        <CartesianGrid stroke={GRID_STROKE} vertical={false} />
        <XAxis dataKey={xKey} tick={AXIS_TICK} tickFormatter={dayTick} tickLine={false} axisLine={{ stroke: GRID_STROKE }} minTickGap={24} />
        <YAxis yAxisId="n" tick={AXIS_TICK} tickLine={false} axisLine={false} allowDecimals={false} width={32} />
        <YAxis yAxisId="p" orientation="right" domain={[0, 100]} tick={AXIS_TICK} tickLine={false} axisLine={false} width={40} tickFormatter={(v) => `${v}%`} />
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          labelStyle={{ color: 'var(--text-muted)' }}
          cursor={{ fill: 'var(--surface-2)', opacity: 0.4 }}
          formatter={(v: unknown, name: unknown) => (name === rate.name ? [`${v}%`, rate.name] : [String(v), String(name)])}
        />
        <Legend wrapperStyle={LEGEND_STYLE} />
        <Bar yAxisId="n" dataKey={bar.key} name={bar.name} fill={bar.color} radius={[2, 2, 0, 0]} maxBarSize={28} />
        <Line yAxisId="p" type="monotone" dataKey={rate.key} name={rate.name} stroke={rate.color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
